import { deleteDB } from 'idb';

import {
  createDeleteIDBValueByKey,
  createGetAllIDBValues,
  createGetIDBValueByKey,
  createSetIDBValue,
  openDB,
} from './indexedDB.utils';
import { CACHE_DB } from '../constants/indexedDB.constants';

const {
  NAME,
  VERSION,
  OBJECT_STORES: { DETAIL_FORM_MODELS, ENTITY_DEFINITIONS },
  KEY_PATHS: { FORM_KEY, FORM_ENTITY_KEY },
} = CACHE_DB;

export const cacheDB = openDB(NAME, VERSION, {
  upgrade(db) {
    for (const storeName of db.objectStoreNames) {
      db.deleteObjectStore(storeName);
    }

    db.createObjectStore(DETAIL_FORM_MODELS, {
      keyPath: FORM_KEY,
    });
    db.createObjectStore(ENTITY_DEFINITIONS, {
      keyPath: FORM_ENTITY_KEY,
    });
  },
}).catch(async (error) => {
  if (error.name === 'VersionError') {
    await deleteDB(NAME);
    window.location.reload();
  }
});

export const setCacheDBValue = createSetIDBValue(cacheDB);

export const deleteCacheDBValueByKey = createDeleteIDBValueByKey(cacheDB);

export const getCacheDBValueByKey = createGetIDBValueByKey(cacheDB);

export const getAllCacheDBValues = createGetAllIDBValues(cacheDB);

export const getAllCacheDBValuesForCurrentUser = async (storeName, keyPath) => {
  const objectStoreValues = await getAllCacheDBValues(storeName);
  const userCacheKey = localStorage.getItem('userCacheKey');

  return objectStoreValues.filter((value) => value[keyPath].split(' + ')[1] === userCacheKey);
};

export const deleteAllCacheDBValuesForCurrentUser = async (storeName, keyPath) => {
  const objectStoreValues = await getAllCacheDBValuesForCurrentUser(storeName, keyPath);

  for (const value of objectStoreValues) {
    await deleteCacheDBValueByKey(storeName, value[keyPath]);
  }
};

export const shouldSetCacheDBValue = (storeName, key, lastModifiedDate) => {
  const entityDefinitionCacheDays = +localStorage.getItem('entityDefinitionCacheDays');
  const detailFormCacheDays = +localStorage.getItem('detailFormCacheDays');
  const isDetailFormsStore = storeName === DETAIL_FORM_MODELS;

  if (detailFormCacheDays === 0 && isDetailFormsStore) {
    return false;
  }

  if (entityDefinitionCacheDays === 0 && !isDetailFormsStore) {
    return false;
  }

  const { entitySchema = {}, forms = {} } = JSON.parse(
    localStorage.getItem('uiCacheObjects') || '{}'
  );
  const isInUiCacheObjects = isDetailFormsStore ? key in forms : key in entitySchema;

  if (!isInUiCacheObjects) {
    return false;
  }

  return isDetailFormsStore
    ? forms[key] === lastModifiedDate
    : entitySchema[key] === lastModifiedDate;
};

export const handleCacheDBExpirationDatesForStore = async (store, storeName, keyPath) => {
  for (const cacheObj of store) {
    if (new Date() > new Date(cacheObj.cacheExpirationDate)) {
      await deleteCacheDBValueByKey(storeName, cacheObj[keyPath]);
    }
  }
};

export const handleCacheDBExpirationDates = async () => {
  const [detailFormModels, entityDefinitions] = await Promise.all([
    getAllCacheDBValuesForCurrentUser(DETAIL_FORM_MODELS, FORM_KEY),
    getAllCacheDBValuesForCurrentUser(ENTITY_DEFINITIONS, FORM_ENTITY_KEY),
  ]);

  await handleCacheDBExpirationDatesForStore(detailFormModels, DETAIL_FORM_MODELS, FORM_KEY);
  await handleCacheDBExpirationDatesForStore(
    entityDefinitions,
    ENTITY_DEFINITIONS,
    FORM_ENTITY_KEY
  );
};

export const handleAppSettingsForCacheDB = async (appSettings) => {
  const entityDefinitionCacheDays = +appSettings['UICache/EntitySchemaCache'];
  const detailFormCacheDays = +appSettings['UICache/FormCache'];

  localStorage.setItem('entityDefinitionCacheDays', entityDefinitionCacheDays);
  localStorage.setItem('detailFormCacheDays', detailFormCacheDays);

  if (entityDefinitionCacheDays === 0) {
    await deleteAllCacheDBValuesForCurrentUser(ENTITY_DEFINITIONS, FORM_ENTITY_KEY);
  }

  if (detailFormCacheDays === 0) {
    await deleteAllCacheDBValuesForCurrentUser(DETAIL_FORM_MODELS, FORM_KEY);
  }

  if (entityDefinitionCacheDays !== 0 || detailFormCacheDays !== 0) {
    handleCacheDBExpirationDates();
  }
};
